/**
 * Read a stored report's payload into what the portal review view shows (Gate B M1 S7).
 *
 * The payload is the orchestrator's `GenerateInsightsResponse` dump, held opaquely by the
 * Report Store, so every field is checked here rather than trusted. A row this code cannot
 * read is skipped, and a payload with nothing readable renders as empty sections, never a
 * crash, because a lab doctor looking at a half-rendered report must still see that it
 * is half-rendered rather than a blank screen.
 *
 * This is a REVIEW view of what the report carries, not a second patient renderer:
 *
 * - no AI narrative (the payload has none, report-summary writes that),
 * - results with their band and severity, Critical first, with the What/Why text and the
 *   sources the patient gets beside them. A lab report (docs#199) leads with the catalog's
 *   display name and the lab's own value, unit and printed range, the way the patient page
 *   does, and keeps our banded number beside it only when we converted,
 * - unrecognised (passthrough) results with their reason, their What/Why and the note the
 *   patient reads in place of an interpretation,
 * - every insight, grouped by type, Critical first inside a group, with its sources.
 *
 * Rows this code cannot read are counted in `skipped`, so the page can say the report is
 * not fully shown rather than look complete.
 *
 * The lab's content span (XS to L) is applied by the BFF before the payload reaches this
 * page, with the release path's own rule, so the insights here are the ones the patient is
 * sent. `readSpan` reads which span that was off the report, never off the payload.
 */

import { t } from "@/i18n";

export type SeverityRank = 0 | 1 | 2;

/** Critical first, then Recommended, then anything else (None, null, unknown). */
export function severityRank(severity: string | null | undefined): SeverityRank {
  const value = (severity ?? "").toLowerCase();
  if (value === "critical") return 0;
  if (value === "recommended") return 1;
  return 2;
}

/** A stable sort by severity rank, so rows of equal severity keep the payload's order. */
export function bySeverity<T>(rows: T[], severityOf: (row: T) => string | null): T[] {
  return rows
    .map((row, index) => ({ row, index, rank: severityRank(severityOf(row)) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.row);
}

/** What `orderReports` reads off a list row. */
export interface OrderableReport {
  generated_at: string;
  highest_severity?: string | null;
}

/**
 * The Reports list order (Baher, 2026-10-04): Critical first, then newest. Two tiers only,
 * a row whose report has any Critical result, then every other row (Recommended, None, null
 * or unknown alike), newest first inside each. Recommended does NOT rank above None here,
 * so this matches the BFF's own sort across pages. A row without `highest_severity` ranks
 * with the non-Critical rows, so a page from a BFF that predates the field is newest
 * first. A `generated_at` that does not parse sorts last. Ties keep the server's order.
 * Returns a new array.
 */
export function orderReports<T extends OrderableReport>(rows: readonly T[]): T[] {
  const time = (row: T): number => {
    const parsed = Date.parse(row.generated_at);
    return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
  };
  return rows
    .map((row, index) => ({
      row,
      index,
      rank: severityRank(row.highest_severity) === 0 ? 0 : 1,
      at: time(row),
    }))
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      if (a.at !== b.at) return a.at > b.at ? -1 : 1;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

export interface ReviewResult {
  /** The catalog symbol (`K`). */
  biomarker: string;
  /** The catalog's display name (`Potassium`, docs#199). Null on a payload from before it. */
  displayName: string | null;
  /** The name the lab sent. On a lab report it is set on every reading, not only on a rename. */
  sentName: string | null;
  band: string;
  severity: string | null;
  inconclusive: boolean;
  /**
   * True when the reading carries the docs#199 lab fields (`display_name`, `value`,
   * `value_text`, `unit`, `lab_reference_range`), which every stored lab report does. The
   * page then leads with the lab's own value and range, as the patient page does.
   */
  labMode: boolean;
  /** The lab's value as printed: `value_text` when sent (keeps `12.0`), else `value`. */
  labValue: string | null;
  /** The lab's unit, before any conversion. */
  labUnit: string | null;
  /** The range the lab printed (`lab_reference_range`), in `labRangeUnit`. */
  labRangeLow: number | null;
  labRangeHigh: number | null;
  labRangeUnit: string | null;
  /** The number we banded, in `unit`. Not the number the lab printed when we converted. */
  value: number | null;
  /**
   * The unit we banded in, the reference range's own unit. The value and the normal range
   * are both in it, so the two always read on one scale.
   */
  unit: string | null;
  /**
   * The unit the lab sent, set only when we converted away from it. On a payload from before
   * docs#199 this is all the page knows of the lab's side: that we converted and from what.
   */
  sentUnit: string | null;
  normalLow: number | null;
  normalHigh: number | null;
  whatWhy: string | null;
  citations: ReviewSource[];
}

/**
 * One source as the page shows it (docs#15 note 16595 item 7): its name, the publisher and
 * year when typed, and a link when it has an `http(s)` address. A source with no usable
 * address stays plain text, and no other scheme is ever made a link.
 */
export interface ReviewSource {
  title: string;
  meta: string | null;
  url: string | null;
}

/**
 * One content notice. `withholds` is false only for a notice that says it held nothing back
 * (no insight withheld, no rule left unevaluated, no marker affected), such as the pregnancy
 * note (docs#72 note 8984). A notice from a payload without those counts is taken to
 * withhold, the title it always had.
 */
export interface ReviewDisclosure {
  note: string;
  withholds: boolean;
}

export interface ReviewPassthrough {
  name: string;
  value: string;
  unit: string;
  reason: string;
  rangeLow: number | null;
  rangeHigh: number | null;
  note: string | null;
  whatWhy: string | null;
}

export interface ReviewInsight {
  id: string;
  type: string;
  title: string | null;
  body: string;
  disclaimer: string | null;
  severity: string | null;
  relatedBiomarkers: string[];
  citations: ReviewSource[];
}

export interface ReviewGroup {
  type: string;
  insights: ReviewInsight[];
}

export interface ReportReview {
  results: ReviewResult[];
  passthrough: ReviewPassthrough[];
  groups: ReviewGroup[];
  disclosures: ReviewDisclosure[];
  /** Rows of results, passthrough results and insights this code could not read. */
  skipped: number;
}

/**
 * The order the insight type groups are shown in: the order the content span adds them
 * (XS results, S clinical-reading, M tips, screening and doctor visit, L the rest), so a
 * reviewer reads the report in the order its content grows. A type not listed here (a new
 * one, or stats, note and the like) follows, in the order it first appears.
 */
export const INSIGHT_TYPE_ORDER: readonly string[] = [
  "clinical-reading",
  "clinical-reading-tip",
  "screening",
  "doctor-visit",
  "clinical-reading-trend",
  "health-risk-desc",
  "health-risk-tip",
  "calculator-health-risk-tip",
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function pair(value: unknown): [number | null, number | null] {
  if (Array.isArray(value) && value.length === 2) return [num(value[0]), num(value[1])];
  return [null, null];
}

/** A string with something in it besides whitespace, else null (report-summary's `text`). */
function nonBlank(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/** `[title](url)` as one whole raw citation, the form report-summary splits. */
const MD_LINK = /^\s*\[(.*?)\]\((.*)\)\s*$/;

/**
 * One line per source, as plain text: the typed citations, or, only when there is no typed
 * one, the untyped markdown strings some insights carry instead (`raw_citations`), verbatim.
 * Never both: that is the rule agreed with report-summary on docs#199 (note 11428), so the
 * doctor sees the sources the patient sees.
 *
 * Which entries count follows report-summary's `citationItems`
 * (`src/components/lab-delivery/format.ts`, mode `rawOnlyWhenNoCitations`) exactly, because
 * "no typed one" decides whether the raw ones show:
 * - a typed citation counts when its title, else its url, else its id, has anything but
 *   whitespace in it, and that is the name shown. One with an empty title and a url is a
 *   source, on both pages.
 * - a raw entry counts when it is not blank. A repeat of an earlier raw entry with the same
 *   title and url (after reading `[title](url)`) is shown once, as the patient page does.
 */
function readCitations(typed: unknown, raw: unknown): ReviewSource[] {
  const sources = records(typed)
    .map((c): ReviewSource | null => {
      const title = nonBlank(c.title) ?? nonBlank(c.url) ?? nonBlank(c.id);
      if (title === null) return null;
      const meta = [nonBlank(c.publisher), num(c.year)?.toString() ?? null].filter(
        (part): part is string => part !== null,
      );
      return {
        title,
        meta: meta.length > 0 ? meta.join(", ") : null,
        url: linkable(nonBlank(c.url)),
      };
    })
    .filter((source): source is ReviewSource => source !== null);
  if (sources.length > 0) return sources;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: ReviewSource[] = [];
  for (const entry of raw) {
    const line = nonBlank(entry);
    if (line === null) continue;
    const link = MD_LINK.exec(line);
    const key = link
      ? `${nonBlank(link[1]) ?? link[2]}\u0000${nonBlank(link[2]) ?? ""}`
      : `${line}\u0000`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (link) {
      const url = linkable(nonBlank(link[2]));
      out.push({ title: nonBlank(link[1]) ?? link[2] ?? line, meta: null, url });
    } else {
      out.push({ title: line, meta: null, url: linkable(line.trim()) });
    }
  }
  return out;
}

/**
 * `value` when it is an absolute `http:` or `https:` address with nothing else in it, else
 * null. Anything else, `javascript:` included, is never made a link.
 */
export function linkable(value: string | null): string | null {
  if (value === null || /\s/.test(value.trim())) return null;
  const candidate = value.trim();
  if (!/^https?:\/\//i.test(candidate)) return null;
  try {
    const url = new URL(candidate);
    return url.protocol === "http:" || url.protocol === "https:" ? candidate : null;
  } catch {
    return null;
  }
}

/** The docs#199 reading fields that mark a reading built on the lab path. */
const LAB_READING_FIELDS = ["display_name", "value", "value_text", "unit", "lab_reference_range"];

/** A value as text, JSON's number as JSON printed it. Null for anything else. */
function valueText(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return str(value);
}

function readResult(row: Record<string, unknown>): ReviewResult | null {
  const biomarker = str(row.biomarker);
  const band = str(row.reading);
  if (biomarker === null || band === null) return null;
  const range = isRecord(row.reference_range) ? row.reference_range : null;
  const [normalLow, normalHigh] = pair(range?.normal);
  // Keyed on its own field name, never on `source` or on shape (docs#199 note 11536).
  const labRange = isRecord(row.lab_reference_range) ? row.lab_reference_range : null;
  return {
    biomarker,
    displayName: str(row.display_name),
    sentName: str(row.sent_name),
    band,
    severity: str(row.severity),
    inconclusive: row.result_state === "inconclusive",
    labMode: LAB_READING_FIELDS.some((field) => field in row),
    labValue: str(row.value_text) ?? valueText(row.value),
    labUnit: str(row.unit),
    labRangeLow: num(labRange?.low),
    labRangeHigh: num(labRange?.high),
    labRangeUnit: str(labRange?.unit),
    value: num(row.banded_value),
    // `banded_value` is in `reference_range.unit`. `sent_unit` is the caller's unit, set
    // only when the engine converted away from it, so it never labels the banded number.
    unit: str(range?.unit),
    sentUnit: str(row.sent_unit),
    normalLow,
    normalHigh,
    whatWhy: str(row.what_why),
    citations: readCitations(row.citations, null),
  };
}

function readPassthrough(row: Record<string, unknown>): ReviewPassthrough | null {
  const name = str(row.name);
  const reason = str(row.reason);
  if (name === null || reason === null) return null;
  const rawValue = row.value;
  const value =
    typeof rawValue === "number" || typeof rawValue === "string" ? String(rawValue) : "";
  const range = isRecord(row.reference_range) ? row.reference_range : null;
  return {
    name,
    value,
    unit: typeof row.unit === "string" ? row.unit : "",
    reason,
    rangeLow: num(range?.low),
    rangeHigh: num(range?.high),
    note: str(row.note),
    whatWhy: str(row.what_why),
  };
}

function readInsight(row: Record<string, unknown>): ReviewInsight | null {
  const id = str(row.id);
  const type = str(row.type);
  const content = isRecord(row.content) ? row.content : null;
  const body = content === null ? null : str(content.body);
  if (id === null || type === null || body === null || content === null) return null;
  const related = Array.isArray(row.related_biomarkers)
    ? row.related_biomarkers.filter((b): b is string => typeof b === "string")
    : [];
  return {
    id,
    type,
    title: str(content.title),
    body,
    disclaimer: str(content.disclaimer),
    severity: str(row.severity),
    relatedBiomarkers: related,
    citations: readCitations(row.citations, row.raw_citations),
  };
}

/**
 * A one-sided range says which side it bounds, so an upper bound of 200 never reads as
 * "–200", which a doctor could take for minus 200. The en dash only joins two bounds.
 */
export function formatRange(low: number | null, high: number | null): string {
  if (low === null && high === null) return "—";
  if (low === null) return `≤ ${high}`;
  if (high === null) return `≥ ${low}`;
  return `${low}–${high}`;
}

/** The content span the BFF filtered the payload to, as the page reads it off the report. */
export interface ReviewSpan {
  span: string;
  /** `config`, `default`, `flag_off` or `fail_closed`, see onboarding-api's `PortalReport`. */
  source: string;
  /** How many insights the span keeps from the patient, not shown on the page. */
  outside: number;
}

/**
 * The span, or null from a BFF that predates it (the payload is then unfiltered, and the
 * page says so).
 */
export function readSpan(report: unknown): ReviewSpan | null {
  if (!isRecord(report)) return null;
  const span = str(report.content_span);
  if (span === null) return null;
  const outside = num(report.insights_outside_span);
  return {
    span,
    source: str(report.content_span_source) ?? "fail_closed",
    outside: outside !== null && outside > 0 ? outside : 0,
  };
}

export function readReview(payload: unknown): ReportReview {
  const body = isRecord(payload) ? payload : {};

  const results = bySeverity(readRows(body.biomarker_readings, readResult), (r) => r.severity);
  const passthrough = readRows(body.passthrough_observations, readPassthrough);
  const insights = readRows(body.insights, readInsight);
  const skipped =
    rowCount(body.biomarker_readings) -
    results.length +
    (rowCount(body.passthrough_observations) - passthrough.length) +
    (rowCount(body.insights) - insights.length);
  const byType = new Map<string, ReviewInsight[]>();
  for (const insight of insights) {
    const list = byType.get(insight.type) ?? [];
    list.push(insight);
    byType.set(insight.type, list);
  }
  const known = INSIGHT_TYPE_ORDER.filter((type) => byType.has(type));
  const unknown = [...byType.keys()].filter((type) => !INSIGHT_TYPE_ORDER.includes(type));
  const groups = [...known, ...unknown].map((type) => ({
    type,
    insights: bySeverity(byType.get(type) ?? [], (i) => i.severity),
  }));

  const disclosures = records(body.content_disclosures)
    .map((d): ReviewDisclosure | null => {
      const note = str(d.note);
      if (note === null) return null;
      const counted = ["insights_withheld", "rules_not_evaluated", "affected_biomarkers"].every(
        (field) => field in d,
      );
      const withholdsNothing =
        counted &&
        (num(d.insights_withheld) ?? 0) === 0 &&
        (num(d.rules_not_evaluated) ?? 0) === 0 &&
        Array.isArray(d.affected_biomarkers) &&
        d.affected_biomarkers.length === 0;
      return { note, withholds: !withholdsNothing };
    })
    .filter((d): d is ReviewDisclosure => d !== null);

  return { results, passthrough, groups, disclosures, skipped };
}

/**
 * The insights the lab's span keeps from the patient (Baher, docs#15 note 16595 item 9), read
 * like the report's own, grouped by type. Empty from a BFF that does not send them.
 */
export function readHeldBack(report: unknown): ReviewGroup[] {
  if (!isRecord(report)) return [];
  return readReview({ insights: report.insights_held_back }).groups;
}

/**
 * The names on an insight's "Results" line: only the markers this report carries (docs#15
 * note 16595 item 11). An insight's `related_biomarkers` is the rule's whole list, every
 * marker the insight is about, so printed as is it named results the patient never had.
 * Each kept marker is shown by its display name when the report has one.
 */
export function relatedInReport(related: string[], results: ReviewResult[]): string[] {
  const byName = new Map(results.map((r) => [r.biomarker, r.displayName ?? r.biomarker]));
  const out: string[] = [];
  for (const symbol of related) {
    const name = byName.get(symbol);
    if (name !== undefined && !out.includes(name)) out.push(name);
  }
  return out;
}

/**
 * Our banded number at the significant figures the lab printed its own value with (docs#15
 * note 16595 item 10, Baher's ruling). Significant figures rather than decimal places, so a
 * unit conversion keeps the measurement's precision: the lab's `210` mg/dL is three figures
 * and reads as 5.43 mmol/L, where three figures of decimal places would have read as 5.
 * Every digit from the first non-zero one counts, trailing zeros included (`0.80` is two).
 * Trailing zeros of the result are dropped. Without a printed numeric value, or one with no
 * non-zero digit, the number is shown as it is. The exact number stays on hover.
 */
export function roundLikeLab(value: number, labValue: string | null): string {
  if (labValue === null) return String(value);
  const match = /^\s*[-+]?(\d*)(?:\.(\d*))?\s*$/.exec(labValue);
  if (match === null) return String(value);
  const digits = `${match[1] ?? ""}${match[2] ?? ""}`.replace(/^0+/, "");
  if (digits === "") return String(value);
  return String(Number(value.toPrecision(Math.min(digits.length, 21))));
}

/**
 * A server instant as a reader would write it, with the timezone named, never raw ISO with
 * microseconds (docs#15 note 16595 item 3). In the lab's timezone when its config sets one,
 * else in the browser's own. An unreadable value is shown as it came.
 */
export function formatWhen(iso: string | null | undefined, timeZone?: string | null): string {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  };
  try {
    return new Intl.DateTimeFormat("en-GB", { ...options, timeZone: timeZone ?? undefined }).format(
      at,
    );
  } catch {
    // A timezone name this browser does not know: the browser's own zone, still named.
    return new Intl.DateTimeFormat("en-GB", options).format(at);
  }
}

/** A `YYYY-MM-DD` date of birth as `14 Mar 1980`. The calendar date, no timezone. */
export function formatBirthDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return value;
  const at = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(at);
}

/** `male` / `female` / `other` in words, else null (shown as not available). */
export function sexLabel(sex: string | null | undefined): string | null {
  if (sex === "male") return t("portal.identity_sex_male");
  if (sex === "female") return t("portal.identity_sex_female");
  if (sex === "other") return t("portal.identity_sex_other");
  return null;
}

/** How many rows a payload list holds, readable or not. Not a list counts as none. */
function rowCount(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

function readRows<T>(value: unknown, read: (row: Record<string, unknown>) => T | null): T[] {
  return records(value)
    .map(read)
    .filter((r): r is T => r !== null);
}

/**
 * What kind of failure an `apiFetch` error is, read from its status line only.
 *
 * The page shows a fixed sentence per kind and NEVER the error's text: `apiFetch` appends
 * the server's own `detail` to the message, and a reviewer's screen is not the place for
 * an upstream's words, whatever they say.
 */
export type ReportErrorKind = "not_found" | "forbidden" | "unavailable";

/** What went wrong with an approve or withdraw (Gate B M2 S11). */
export type ReportActErrorKind = ReportErrorKind | "conflict";

export function reportActErrorKind(error: unknown): ReportActErrorKind {
  const message = error instanceof Error ? error.message : "";
  if (/^API request failed: 409/.test(message)) return "conflict";
  return reportErrorKind(error);
}

export function reportErrorKind(error: unknown): ReportErrorKind {
  const message = error instanceof Error ? error.message : "";
  const match = /^API request failed: (\d{3})/.exec(message);
  if (match?.[1] === "404") return "not_found";
  if (match?.[1] === "403") return "forbidden";
  return "unavailable";
}
